import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, TimerReset, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
export default function DualOtpVerification() {
  const OTP_LENGTH = 6;
  const [emailOtp, setEmailOtp] = useState(new Array(OTP_LENGTH).fill(''));
  const [mobileOtp, setMobileOtp] = useState(new Array(OTP_LENGTH).fill(''));
  const [timer, setTimer] = useState(120);
  const [resendAvailable, setResendAvailable] = useState(false);
  const navigate = useNavigate();
  const emailRefs = useRef([]);
  const mobileRefs = useRef([]);

  useEffect(() => {
    if (timer === 0) {
      setResendAvailable(true);
      return;
    }
    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (value, index, type) => {
    if (!/^\d?$/.test(value)) return;
    const otpState = type === 'email' ? [...emailOtp] : [...mobileOtp];
    otpState[index] = value;

    if (type === 'email') {
      setEmailOtp(otpState);
      if (value && index < OTP_LENGTH - 1) emailRefs.current[index + 1].focus();
    } else {
      setMobileOtp(otpState);
      if (value && index < OTP_LENGTH - 1) mobileRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index, type) => {
    const otpState = type === 'email' ? emailOtp : mobileOtp;
    const refs = type === 'email' ? emailRefs : mobileRefs;

    if (e.key === 'Backspace' && !otpState[index] && index > 0) {
      refs.current[index - 1].focus();
    }
  };

  const handleSubmit = () => {
    const emailCode = emailOtp.join('');
    const mobileCode = mobileOtp.join('');

    if (emailCode.length !== OTP_LENGTH || mobileCode.length !== OTP_LENGTH) {
        toast.error('Please enter both OTPs fully.');
      return;
    }

    toast.success('OTPs verified successfully!');
    // Navigate to next step or perform API call
    navigate('/final-otp');
    // TODO: API call
  };

  const handleResend = () => {
    setEmailOtp(new Array(OTP_LENGTH).fill(''));
    setMobileOtp(new Array(OTP_LENGTH).fill(''));
    setTimer(120);
    setResendAvailable(false);
    emailRefs.current[0].focus();
    // TODO: trigger resend logic
  };

  return (
    <div className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
    <ToastContainer/>
      {/* Background Video */}
      <video autoPlay muted loop className="absolute inset-0 w-full h-full object-cover z-0">
        <source src="./assets/login_video.mp4" type="video/mp4" />
      </video>

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-10" />

      {/* Form Card */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-20 max-w-xl w-full bg-white/20 backdrop-blur-xl border border-white/30 p-8 rounded-2xl shadow-2xl text-white"
      >
        <div className="flex items-center justify-center gap-3 mb-6">
          <ShieldCheck size={28} className="text-cyan-300" />
          <h2 className="text-2xl font-semibold">OTP Verification</h2>
        </div>

        {/* Email OTP */}
        <div className="mb-6">
          <label className="text-sm mb-2 block flex items-center gap-2">
            <Mail className="text-cyan-400" size={18} /> Email OTP
          </label>
          <div className="flex justify-center gap-3">
            {emailOtp.map((digit, index) => (
              <motion.input
                key={index}
                ref={(el) => (emailRefs.current[index] = el)}
                type="text"
                value={digit}
                maxLength={1}
                onChange={(e) => handleChange(e.target.value, index, 'email')}
                onKeyDown={(e) => handleKeyDown(e, index, 'email')}
                className="w-10 h-10 text-center text-lg text-gray-800 font-bold bg-white rounded-md shadow focus:ring-2 focus:ring-cyan-400 outline-none"
                whileFocus={{ scale: 1.1 }}
              />
            ))}
          </div>
        </div>

        {/* Mobile OTP */}
        <div className="mb-6">
          <label className="text-sm mb-2 block flex items-center gap-2">
            <Phone className="text-cyan-400" size={18} /> Mobile OTP
          </label>
          <div className="flex justify-center gap-3">
            {mobileOtp.map((digit, index) => (
              <motion.input
                key={index}
                ref={(el) => (mobileRefs.current[index] = el)}
                type="text"
                value={digit}
                maxLength={1}
                onChange={(e) => handleChange(e.target.value, index, 'mobile')}
                onKeyDown={(e) => handleKeyDown(e, index, 'mobile')}
                className="w-10 h-10 text-center text-lg text-gray-800 font-bold bg-white rounded-md shadow focus:ring-2 focus:ring-cyan-400 outline-none"
                whileFocus={{ scale: 1.1 }}
              />
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          className="w-full bg-cyan-600 text-white py-2 rounded-md hover:bg-cyan-700 transition-all flex items-center justify-center gap-2"
        >
          Verify OTPs
        </button>

        {/* Resend */}
        <div className="mt-6 text-sm text-center">
          {resendAvailable ? (
            <button
              onClick={handleResend}
              className="text-cyan-400 hover:underline flex items-center justify-center gap-1"
            >
              <TimerReset size={16} />
              Resend OTPs
            </button>
          ) : (
            <p className="text-gray-300">
              Resend in <span className="font-medium">{Math.floor(timer / 60)}:{String(timer % 60).padStart(2, '0')}</span>
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
}
